import React, { FC, useMemo } from 'react';
import { TenantListSortOption } from '../../utils/TenantUtils';
import { IconBookmark, IconUser } from '../icons';
import { useDeviceOrientation } from '../../hooks/useDeviceOrientation';
import { getShortFloorLabel } from '../../utils/helpers';
import { TenantWithFloor } from '../../types/unit';
import { cn } from '../../utils/classNames';

interface MergedTenantListItemProps {
  tenants: TenantWithFloor[];
  sort?: TenantListSortOption;
  onClick?: (tenant: TenantWithFloor) => void;
}

const getTenantDisplayName = (
  tenant: TenantWithFloor,
  sort: TenantListSortOption,
): { name1: string; name2: string } => {
  if (tenant.type === 'company') {
    return { name1: tenant.company.name, name2: '' };
  }

  const { first_name, last_name } = tenant.individual;
  if (!last_name) {
    return { name1: first_name, name2: '' };
  }

  return sort === 'name'
    ? { name1: first_name, name2: last_name }
    : { name1: last_name, name2: first_name };
};

const MergedTenantListItem: FC<MergedTenantListItemProps> = ({
  tenants,
  sort = 'last_name',
  onClick,
}) => {
  const { portrait } = useDeviceOrientation();
  const representative = tenants[0];

  const iconSize = portrait ? 'size-8 min-w-8' : 'size-6 min-w-6';

  const tenantEntries = useMemo(
    () =>
      tenants.map((tenant) => ({
        tenant,
        ...getTenantDisplayName(tenant, sort),
      })),
    [tenants, sort],
  );

  return (
    <li className="flex items-center gap-4 rounded border border-gray-medium px-4 py-2">
      <div
        className={cn(
          'flex min-w-0 grow flex-col gap-1',
          portrait && 'text-lg',
        )}
      >
        {tenantEntries.map(({ tenant, name1, name2 }) => (
          <div
            key={tenant.id}
            className="flex items-center gap-3"
            onClick={() => onClick?.(tenant)}
          >
            <div className={cn('flex shrink-0 items-center', iconSize)}>
              {tenant.type === 'company' ? (
                <>
                  {tenant.company.logo ? (
                    <img
                      src={tenant.company.logo}
                      alt="logo"
                      className="h-full w-full rounded-sm object-contain"
                    />
                  ) : (
                    <IconBookmark className="h-full w-full" />
                  )}
                </>
              ) : (
                <IconUser className="h-full w-full" />
              )}
            </div>
            <div className="truncate">
              <span className={sort !== 'floor' ? 'font-bold' : ''}>
                {name1}
              </span>
              {name2 && (
                <>
                  {sort !== 'name' && ','}
                  <span className="ml-1">{name2}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {representative.type !== 'company' && representative.object_id ? (
        <i className="shrink-0 text-right">
          {representative.internal_object_id && (
            <span className="mr-10">{representative.internal_object_id}</span>
          )}
          {representative.object_id}
        </i>
      ) : null}
      {sort !== 'floor' ? (
        <span className="shrink-0">
          {getShortFloorLabel(representative.floor_nr.toString())}
        </span>
      ) : null}
    </li>
  );
};

export default MergedTenantListItem;
